import { createContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'

export const TEXT_SIZES = ['small', 'default', 'large', 'larger'] as const
export type TextSize = (typeof TEXT_SIZES)[number]
export const TEXT_SIZE_KEY = 'text-size'

export interface ThemeContextValue {
  /**
   * Active theme. `true` = dark (the default). Canvas art components
   * (RuneConstellation, VocabularyBrain) subscribe
   * to this value and MUST destroy + recreate their engine with the active
   * palette when it changes, so no stale-colour frame persists after a toggle.
   */
  dark: boolean
  toggleDark: () => void
  /** App-wide text-size step, mirrored to `<html data-text-size>` (research D1). */
  textSize: TextSize
  setTextSize: (size: TextSize) => void
}

export const ThemeContext = createContext<ThemeContextValue>({
  dark: false,
  toggleDark: () => {},
  textSize: 'default',
  setTextSize: () => {},
})

function isTextSize(v: unknown): v is TextSize {
  return typeof v === 'string' && (TEXT_SIZES as readonly string[]).includes(v)
}

function readTextSize(): TextSize {
  if (typeof window === 'undefined') return 'default'
  try {
    const stored = localStorage.getItem(TEXT_SIZE_KEY)
    return isTextSize(stored) ? stored : 'default'
  } catch {
    return 'default'
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [dark, setDark] = useState(() => {
    // Dark is the default theme; only an explicit stored 'light' opts out.
    if (typeof window === 'undefined') return true
    return localStorage.getItem('theme') !== 'light'
  })
  const [textSize, setTextSize] = useState<TextSize>(readTextSize)

  useEffect(() => {
    localStorage.setItem('theme', dark ? 'dark' : 'light')
    document.documentElement.classList.toggle('dark', dark)
  }, [dark])

  useEffect(() => {
    try {
      localStorage.setItem(TEXT_SIZE_KEY, textSize)
    } catch {
      // Storage denied: the attribute still applies for this page load.
    }
    document.documentElement.dataset.textSize = textSize
  }, [textSize])

  return (
    <ThemeContext.Provider
      value={{ dark, toggleDark: () => setDark((d) => !d), textSize, setTextSize }}
    >
      {children}
    </ThemeContext.Provider>
  )
}
